import * as THREE from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';/**
 * 把世界坐标投影到当前视口屏幕坐标。
 *
 * 规则说明：
 * 1. 基准点在线上的默认方向应与用户当前视口中看到的左右方向一致。
 * 2. 因此需要先把端点投影到屏幕空间，再比较像素坐标。
 * 3. 若视口尚未就绪，则返回 null，由调用方执行世界坐标兜底排序。
 *
 * @param viewer 视图实例。
 * @param point 世界坐标点。
 * @returns 屏幕坐标；无法投影时返回 null。
 */
export function projectWorldPointToScreen(
  viewer: Viewer,
  point: THREE.Vector3,
): THREE.Vector2 | null {
  if (!viewer.camera || !viewer.renderer) {
    return null;
  }

  const viewportSize = new THREE.Vector2();
  viewer.renderer.getSize(viewportSize);
  if (viewportSize.x <= 0 || viewportSize.y <= 0) {
    return null;
  }

  const ndcPoint = point.clone().project(viewer.camera);
  if (!Number.isFinite(ndcPoint.x) || !Number.isFinite(ndcPoint.y) || ndcPoint.z > 1) {
    return null;
  }

  return new THREE.Vector2(
    (ndcPoint.x * 0.5 + 0.5) * viewportSize.x,
    (1 - (ndcPoint.y * 0.5 + 0.5)) * viewportSize.y,
  );
}

/**
 * 比较两个点在默认直线方向下的先后顺序。
 *
 * 说明：
 * 1. 优先采用屏幕空间 x 比较，确保默认方向与当前视口中的左到右一致。
 * 2. 当两个点在屏幕上几乎垂直时，再按屏幕 y 稳定排序。
 * 3. 若当前视口不可投影，则退回世界坐标比较，保证命令链路可继续工作。
 *
 * @param viewer 视图实例。
 * @param lhs 左侧点。
 * @param rhs 右侧点。
 * @returns 小于 0 表示 lhs 更靠前；大于 0 表示 rhs 更靠前；等于 0 表示视为同点。
 */
export function comparePointForDefaultLinearDirection(
  viewer: Viewer,
  lhs: THREE.Vector3,
  rhs: THREE.Vector3,
): number {
  const epsilon = 1e-9;
  const lhsScreenPoint = projectWorldPointToScreen(viewer, lhs);
  const rhsScreenPoint = projectWorldPointToScreen(viewer, rhs);
  if (lhsScreenPoint && rhsScreenPoint) {
    if (Math.abs(lhsScreenPoint.x - rhsScreenPoint.x) > epsilon) {
      return lhsScreenPoint.x - rhsScreenPoint.x;
    }
    if (Math.abs(lhsScreenPoint.y - rhsScreenPoint.y) > epsilon) {
      return lhsScreenPoint.y - rhsScreenPoint.y;
    }
  }

  if (Math.abs(lhs.x - rhs.x) > epsilon) {
    return lhs.x - rhs.x;
  }
  if (Math.abs(lhs.y - rhs.y) > epsilon) {
    return lhs.y - rhs.y;
  }
  if (Math.abs(lhs.z - rhs.z) > epsilon) {
    return lhs.z - rhs.z;
  }
  return 0;
}

/**
 * 归一化线性实体端点方向。
 *
 * 说明：
 * 1. 仅用于线性 FeatureLine 候选，不作用于圆弧或圆等需要保留原始参数方向的实体。
 * 2. 返回值中的 start 永远表示当前视口语义下的默认起点。
 * 3. AlongCurve.directionPolicy 为 natural 时将直接基于该起点累积距离。
 *
 * @param viewer 视图实例。
 * @param endpoints 原始端点。
 * @returns 归一化后的端点。
 */
export function normalizeLinearEndpointsDirection(
  viewer: Viewer,
  endpoints: LineSegmentEndpoints,
): LineSegmentEndpoints {
  return comparePointForDefaultLinearDirection(viewer, endpoints.start, endpoints.end) <= 0
    ? {
      start: endpoints.start.clone(),
      end: endpoints.end.clone(),
    }
    : {
      start: endpoints.end.clone(),
      end: endpoints.start.clone(),
    };
}
